import React, { useState } from 'react';
import { Container, Row, Col } from "react-bootstrap";
import { 
  Zap, 
  Sliders,
  BarChart2, 
  Headphones, 
  Users,
  Award,
  Shield,
  Clock,
  CheckCircle,
  TrendingUp,
  Globe,
  Target,
  Star
} from "react-feather";

const styles = {
  section: {
    backgroundColor: '#1e293b',
    color: '#ffffff',
    padding: '120px 0',
    position: 'relative',
    overflow: 'hidden',
  },
  gradientBg: {
    position: 'absolute',
    top: '0',
    left: '0',
    right: '0',
    bottom: '0',
    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)',
    zIndex: '0',
  },
  title: {
    fontSize: '3.5rem',
    fontWeight: '800',
    color: '#ffffff',
    marginBottom: '1rem',
    textAlign: 'center',
  },
  subtitle: {
    fontSize: '1.2rem',
    color: '#cbd5e1',
    textAlign: 'center',
    maxWidth: '700px',
    margin: '0 auto 60px',
    lineHeight: '1.6',
  },
  featureCard: {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderRadius: '20px',
    padding: '40px 30px',
    textAlign: 'center',
    height: '100%',
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    backdropFilter: 'blur(10px)',
  },
  featureCardHover: {
    transform: 'translateY(-10px)',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderColor: 'rgba(59, 130, 246, 0.5)',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
  },
  iconWrapper: {
    width: '80px',
    height: '80px',
    borderRadius: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 25px',
    backgroundColor: 'rgba(59, 130, 246, 0.1)',
    transition: 'all 0.3s ease',
  },
  iconWrapperHover: {
    backgroundColor: 'rgba(59, 130, 246, 0.2)',
    transform: 'scale(1.1)',
  },
  featureTitle: {
    fontSize: '1.5rem',
    fontWeight: '700',
    color: '#ffffff',
    marginBottom: '15px',
  },
  featureDescription: {
    fontSize: '1rem',
    color: '#cbd5e1',
    lineHeight: '1.6',
    marginBottom: '0',
  },
  statsContainer: {
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    borderRadius: '25px',
    padding: '60px 40px',
    marginTop: '80px',
    border: '1px solid rgba(255, 255, 255, 0.05)',
  },
  statNumber: {
    fontSize: '3rem',
    fontWeight: '800',
    color: '#3b82f6',
    marginBottom: '10px',
    lineHeight: '1',
  },
  statLabel: {
    fontSize: '1.1rem',
    color: '#cbd5e1',
    fontWeight: '500',
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    backgroundColor: 'rgba(16, 185, 129, 0.2)',
    color: '#10b981',
    padding: '8px 20px',
    borderRadius: '20px',
    fontSize: '0.9rem',
    fontWeight: '600',
    marginBottom: '20px',
  },
  comparisonSection: {
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    borderRadius: '25px',
    padding: '50px',
    marginTop: '80px',
  },
  comparisonTitle: {
    fontSize: '2rem',
    fontWeight: '700',
    color: '#ffffff',
    marginBottom: '40px',
    textAlign: 'center',
  },
  comparisonRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 0',
    borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
  },
  comparisonItem: {
    fontSize: '1.1rem',
    fontWeight: '500',
  },
  vixoColumn: {
    color: '#10b981',
  },
  othersColumn: {
    color: '#ef4444',
  },
  checkIcon: {
    color: '#10b981',
  },
  xIcon: {
    color: '#ef4444',
  },
  ctaButton: {
    background: 'linear-gradient(45deg, #3b82f6, #8b5cf6)',
    border: 'none',
    color: '#ffffff',
    padding: '18px 45px',
    fontSize: '1.1rem',
    fontWeight: '600',
    borderRadius: '50px',
    transition: 'all 0.3s ease',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '12px',
    marginTop: '50px',
  },
  ctaButtonHover: {
    transform: 'translateY(-3px)',
    boxShadow: '0 15px 30px rgba(59, 130, 246, 0.4)',
  },
};

function WhyVixo() {
  const [hoverStates, setHoverStates] = useState({});

  const features = [
    {
      icon: <Zap size={32} />,
      title: "Fast Delivery",
      description: "We deliver projects 40% faster than industry average without compromising on quality.",
      color: '#f59e0b',
    },
    {
      icon: <Sliders size={32} />,
      title: "Creative Design",
      description: "Award-winning designs that combine aesthetics with functionality and user experience.",
      color: '#8b5cf6',
    },
    {
      icon: <BarChart2 size={32} />,
      title: "Measurable Results",
      description: "Data-driven strategies that deliver real ROI and measurable business growth.",
      color: '#10b981',
    },
    {
      icon: <Headphones size={32} />,
      title: "Dedicated Support",
      description: "24/7 premium support with dedicated account managers and technical experts.",
      color: '#3b82f6',
    },
    {
      icon: <Users size={32} />,
      title: "Expert Team",
      description: "Certified professionals with 10+ years of industry experience across all domains.",
      color: '#ec4899',
    },
    {
      icon: <Shield size={32} />,
      title: "Security First",
      description: "Enterprise-grade security protocols to protect your data and digital assets.",
      color: '#06b6d4',
    },
  ];

  const stats = [
    { number: "98%", label: "Client Satisfaction Rate", icon: <Award size={24} /> },
    { number: "24/7", label: "Support Availability", icon: <Clock size={24} /> },
    { number: "223+", label: "Projects Delivered", icon: <CheckCircle size={24} /> },
    { number: "40%", label: "Faster Delivery", icon: <TrendingUp size={24} /> },
  ];

  const comparisons = [
    { feature: "Project Delivery Time", vixo: "2-4 Weeks", others: "4-8 Weeks" },
    { feature: "Support Response Time", vixo: "< 1 Hour", others: "24-48 Hours" },
    { feature: "Design Revisions", vixo: "Unlimited", others: "2-3 Revisions" },
    { feature: "Security Protocols", vixo: "Enterprise Grade", others: "Basic" },
    { feature: "Team Expertise", vixo: "3+ Years Avg", others: "2-3 Years Avg" },
  ];

  return (
    <section id="why" style={styles.section}>
      <div style={styles.gradientBg}></div>
      
      <Container style={{ position: 'relative', zIndex: 1 }}>
        {/* Header */}
        <div className="text-center mb-5">
          <div style={styles.badge}>
            <Star size={16} />
            Industry Leader Since 2015
          </div>
          
          <h2 style={styles.title}>
            Why Choose <span style={{ color: '#3b82f6' }}>VIXO</span>?
          </h2>
          
          <p style={styles.subtitle}>
            We're not just another agency. We're your strategic partner in digital 
            transformation, combining technical excellence with creative innovation 
            to deliver exceptional results.
          </p>
        </div>

        {/* Features Grid */}
        <Row className="g-4">
          {features.map((feature, index) => (
            <Col lg={4} md={6} key={index}>
              <div
                style={{
                  ...styles.featureCard,
                  ...(hoverStates[`feature-${index}`] ? styles.featureCardHover : {})
                }}
                onMouseEnter={() => setHoverStates({...hoverStates, [`feature-${index}`]: true})}
                onMouseLeave={() => setHoverStates({...hoverStates, [`feature-${index}`]: false})}
              >
                <div
                  style={{
                    ...styles.iconWrapper,
                    backgroundColor: `${feature.color}20`,
                    ...(hoverStates[`feature-${index}`] ? styles.iconWrapperHover : {})
                  }}
                >
                  <div style={{ color: feature.color }}>
                    {feature.icon}
                  </div>
                </div>
                
                <h3 style={styles.featureTitle}>{feature.title}</h3>
                <p style={styles.featureDescription}>{feature.description}</p>
              </div>
            </Col>
          ))}
        </Row>

        {/* Stats Section */}
        <div style={styles.statsContainer}>
          <Row className="text-center">
            {stats.map((stat, index) => (
              <Col md={3} sm={6} key={index} className="mb-4 mb-md-0">
                <div style={{ color: '#3b82f6', marginBottom: '15px' }}>
                  {stat.icon}
                </div>
                <div style={styles.statNumber}>{stat.number}</div>
                <div style={styles.statLabel}>{stat.label}</div>
              </Col>
            ))}
          </Row>
        </div>

        {/* Comparison Section */}
        <div style={styles.comparisonSection}>
          <h3 style={styles.comparisonTitle}>VIXO vs Others</h3>
          
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            {/* Header */}
            <div style={{ 
              ...styles.comparisonRow, 
              borderBottom: '2px solid rgba(255, 255, 255, 0.1)',
              fontWeight: '600',
            }}>
              <div style={styles.comparisonItem}>Feature</div>
              <div style={{...styles.comparisonItem, ...styles.vixoColumn}}>VIXO</div>
              <div style={{...styles.comparisonItem, ...styles.othersColumn}}>Others</div>
            </div>
            
            {/* Rows */}
            {comparisons.map((comparison, index) => (
              <div key={index} style={styles.comparisonRow}>
                <div style={styles.comparisonItem}>{comparison.feature}</div>
                <div style={{...styles.comparisonItem, ...styles.vixoColumn}}>
                  <CheckCircle size={18} style={{...styles.checkIcon, marginRight: '10px', verticalAlign: 'middle' }} />
                  {comparison.vixo}
                </div>
                <div style={{...styles.comparisonItem, ...styles.othersColumn}}>
                  <div style={{...styles.xIcon, display: 'inline-block', marginRight: '10px' }}>✗</div>
                  {comparison.others}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <button
            style={{
              ...styles.ctaButton,
              ...(hoverStates.cta ? styles.ctaButtonHover : {})
            }}
            onMouseEnter={() => setHoverStates({...hoverStates, cta: true})}
            onMouseLeave={() => setHoverStates({...hoverStates, cta: false})}
          >
            <Globe size={20} />
            Start Your Digital Transformation Today
            <Target size={20} />
          </button>
          
          <p style={{ 
            color: '#cbd5e1', 
            marginTop: '30px', 
            fontSize: '1rem',
            maxWidth: '600px',
            marginLeft: 'auto',
            marginRight: 'auto',
          }}>
            Join 500+ successful businesses that have transformed their digital presence with VIXO.
          </p>
        </div>
      </Container>

      {/* Decorative Elements */}
      <div style={{
        position: 'absolute',
        top: '50%',
        left: '5%',
        width: '300px',
        height: '300px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(59, 130, 246, 0.1) 0%, transparent 70%)',
        zIndex: '0',
      }}></div>
      
      <div style={{
        position: 'absolute',
        bottom: '10%',
        right: '5%',
        width: '200px',
        height: '200px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(16, 185, 129, 0.1) 0%, transparent 70%)',
        zIndex: '0',
      }}></div>

      <style>
        {`
          @keyframes float {
            0%, 100% {
              transform: translateY(0);
            }
            50% {
              transform: translateY(-10px);
            }
          }
          
          .feature-card {
            animation: float 6s ease-in-out infinite;
            animation-delay: calc(var(--index) * 0.5s);
          }
          
          @media (max-width: 768px) {
            .section {
              padding: 60px 0 !important;
            }
            
            .title {
              font-size: 2.5rem !important;
            }
            
            .stats-container {
              padding: 30px 20px !important;
            }
            
            .comparison-section {
              padding: 30px 20px !important;
            }
            
            .comparison-row {
              flex-direction: column;
              text-align: center;
              gap: 10px;
            }
          }
        `}
      </style>
    </section>
  );
}

export default WhyVixo;